import { CameraPreview } from '@/components/CameraPreview'
import { Card } from '../../Card'
import { CardHeader } from '../../CardHeader'
import CameraIcon from '@/CustomIcons/CameraIcon'
import { useUserContext } from '@/UserContext'
import { useMemo } from 'react'
import MirrorIcon from '@/CustomIcons/MirrorIcon'
import { Checkbox } from '@/UIComponents2/Checkbox'
import VirtualBackgroundIcon from '@/CustomIcons/VirtualBackgroundIcon'
import { SimpleSelect } from '@/UIComponents2/Select/SimpleSelect'
import { useTranslation } from 'react-i18next'
import * as Constants from '@/services/Constants'
import { Button } from '@/UIComponents2/Button'
import { LocalVideoTrack } from 'livekit-client'
import { useAppContext } from '@/AppContext'
import useAnalytics from '@/services/useAnalytics'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import { cn } from '@/utlis/cn'
import useAppSettingsActions from '@/actions/useAppSettingsActions'
import Tooltip from '@/UIComponents2/Tooltip'
import { CameraSelect } from '@/components/CameraSelect'
import { overlayBgClasses, OverlayControlButton } from '@/components/OverlayControlButton'
import useVirtualBackgroundActions from '@/services/useVirtualBackgroundActions'

export function CameraSection() {
    const { t } = useTranslation()
    const [appState, appDispatch] = useAppContext()
    const [userState] = useUserContext()

    const { updateAppSettingItem } = useAppSettingsActions()

    const aspectRatioValue = useMemo(() => {
        if (appState.appSettings.videoOptions?.optionAspectRatio) {
            const a = appState.appSettings.videoOptions.optionAspectRatio
            if (typeof a === 'number') {
                return a.toFixed(3)
            }
            if (a === '_undef') {
                return '_best'
            }
            return a
        }
        return '_best'
    }, [appState.appSettings.videoOptions?.optionAspectRatio])

    const frameRateValue = useMemo(() => {
        if (appState.appSettings.videoOptions?.optionFramerate) {
            const a = appState.appSettings.videoOptions.optionFramerate
            if (typeof a === 'number') {
                return a.toFixed(0)
            }
            if (a === '_undef') {
                return '_best'
            }
            return a
        }
        return '_best'
    }, [appState.appSettings.videoOptions?.optionFramerate])

    const videoQualityValue = useMemo(() => {
        return Constants.video_width_presets.includes(appState.appSettings.videoOptions?.optionWidth || -1)
            ? (appState.appSettings.videoOptions?.optionWidth?.toFixed(0) ?? '_default')
            : '_default'
    }, [appState.appSettings.videoOptions?.optionWidth])

    const videoQualityOptions = useMemo(() => {
        let items = [{ value: '_default', label: t('Default') }]
        for (const presetWidth of Constants.video_width_presets) {
            const aspectRatio = userState.mediaStreams.videoInfo?.settings?.aspectRatio ?? 16 / 9
            const height = Math.floor(presetWidth / parseFloat(aspectRatio.toString()))
            const label = `${presetWidth}x${height}`
            items.push({ value: presetWidth.toFixed(0), label })
        }
        return items
    }, [userState.mediaStreams.videoInfo?.settings?.aspectRatio])

    return (
        <Card className="flex flex-col gap-[20px]">
            <CardHeader icon={CameraIcon} title="Camera" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-[30px]">
                <div className="relative">
                    <CameraPreview
                        videoTrack={userState.mediaStreams.video}
                        isPermissionsDenied={false /*TODO: implement me*/}
                        aspectRatio="4/3"
                    />

                    <div className="absolute bottom-0 left-2 right-2 pb-2 z-10">
                        <CameraSelect className="w-full" triggerClassName={overlayBgClasses} />
                    </div>

                    <div className="absolute top-2 right-2 z-10">
                        <Tooltip text={appState.appSettings.mirrorCamera ? `Turn off mirroring` : `Turn on mirroring`}>
                            <div>
                                <OverlayControlButton
                                    onClick={() =>
                                        updateAppSettingItem('mirrorCamera', !appState.appSettings.mirrorCamera)
                                    }
                                >
                                    <MirrorIcon style={{ fontSize: '1.3rem' }} />
                                </OverlayControlButton>
                            </div>
                        </Tooltip>
                    </div>
                </div>
                <div className="flex flex-col justify-between gap-[15px]">
                    <div className="flex flex-col gap-[16px]">
                        <Checkbox
                            onChange={(v) => updateAppSettingItem('noSelectDefaultCamera', v)}
                            checked={appState.appSettings.noSelectDefaultCamera}
                            label="Don't select camera on startup"
                            labelClassName="font-light"
                        />
                        <Checkbox
                            onChange={(v) => updateAppSettingItem('noShowEmptyCamerasOnGallery', v)}
                            checked={appState.appSettings.noShowEmptyCamerasOnGallery}
                            label="Don't show empty cameras in gallery"
                            labelClassName="font-light"
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <SimpleSelect
                            className="md:col-span-2"
                            value={videoQualityValue}
                            label="Quality"
                            items={videoQualityOptions}
                            widthMatching
                            onChange={(value) => {
                                let newQuality = null
                                if (value !== '_default') {
                                    newQuality = parseInt(value)
                                }
                                appDispatch({
                                    type: 'update_app_settings',
                                    value: {
                                        videoOptions: {
                                            ...(appState.appSettings.videoOptions || {}),
                                            optionWidth: newQuality,
                                        },
                                    },
                                })
                            }}
                        />
                        <SimpleSelect
                            className=""
                            label="Frame Rate"
                            value={frameRateValue}
                            onChange={(value) => {
                                let newFrameRate = null
                                if (value !== '_best' && value !== '_undef') {
                                    newFrameRate = parseInt(value)
                                }

                                appDispatch({
                                    type: 'update_app_settings',
                                    value: {
                                        videoOptions: {
                                            ...(appState.appSettings.videoOptions || {}),
                                            optionFramerate: newFrameRate,
                                        },
                                    },
                                })
                            }}
                            items={[
                                { value: '_best', label: 'Default' },
                                { value: '60', label: '60 fps' },
                                { value: '30', label: '30 fps' },
                                { value: '24', label: '24 fps' },
                                { value: '20', label: '20 fps' },
                                { value: '16', label: '16 fps' },
                                { value: '10', label: '10 fps' },
                                { value: '8', label: '8 fps' },
                                { value: '5', label: '5 fps' },
                            ]}
                            widthMatching
                        />
                        <SimpleSelect
                            className=""
                            label="Aspect"
                            value={aspectRatioValue}
                            onChange={(value) => {
                                let newAspectRatio = null
                                if (value !== '_undef' && value !== '_best') {
                                    newAspectRatio = parseFloat(value)
                                }
                                appDispatch({
                                    type: 'update_app_settings',
                                    value: {
                                        videoOptions: {
                                            ...(appState.appSettings.videoOptions || {}),
                                            optionAspectRatio: newAspectRatio,
                                        },
                                    },
                                })
                            }}
                            items={[
                                { value: '_best', label: 'Default' },
                                { value: '1.777', label: '16/9' },
                                { value: '1.250', label: '5/4' },
                                { value: '1.333', label: '4/3' },
                            ]}
                            widthMatching
                        />
                    </div>
                </div>
            </div>
            <VirtualBackgrouindSection />
        </Card>
    )
}

function VirtualBackgrouindSection() {
    const { t } = useTranslation()
    const [userState] = useUserContext()
    const { logUserActivityWithContext } = useAnalytics()
    const { setVirtualBackground } = useVirtualBackgroundActions()

    const localTrack = useMemo(() => {
        if (userState.mediaStreams.video instanceof LocalVideoTrack) {
            return userState.mediaStreams.video
        }
        return null
    }, [userState.mediaStreams.video])

    const currentProcessor = userState.mediaStreams.videoProcessor
    const onChooseVirtualBackground = async (
        background: Constants.RuntimeConstants_BackgroundImage | 'blur' | null
    ) => {
        setVirtualBackground(
            background && typeof background === 'object' ? background.name : background,
            localTrack,
            currentProcessor
        )

        if (!background) {
            logUserActivityWithContext(AnalyticsEvents.VIRTUAL_BACKGROUND_DISABLED)
        } else {
            logUserActivityWithContext(AnalyticsEvents.VIRTUAL_BACKGROUND_PREVIEW_CHANGED, {
                backgroundName: background === 'blur' ? 'blur' : background.name,
            })
        }
    }

    return (
        <>
            <CardHeader icon={VirtualBackgroundIcon} title="Virtual Background" className="pt-[10px]" />
            <div className="flex flex-wrap gap-[4px]">
                <VirtualBackgroundButton
                    active={userState.mediaSelection.selectedVirtualBackground === null}
                    background="none"
                    onClick={() => onChooseVirtualBackground(null)}
                    name={t('None')}
                />
                <VirtualBackgroundButton
                    active={userState.mediaSelection.selectedVirtualBackground === 'blur'}
                    background="blur"
                    onClick={() => onChooseVirtualBackground('blur')}
                    name={t('Blur')}
                />
                {Constants.virtual_backgrounds?.map((background) => (
                    <VirtualBackgroundButton
                        active={userState.mediaSelection.selectedVirtualBackground === background.name}
                        key={background.id}
                        background={background}
                        onClick={() => onChooseVirtualBackground(background)}
                        name={background.name}
                    />
                ))}
            </div>
        </>
    )
}

type VirtualBackgroundButtonProps = {
    active: boolean
    background: 'none' | 'blur' | Constants.RuntimeConstants_BackgroundImage
    onClick: () => void
    name: string
}
function VirtualBackgroundButton(props: VirtualBackgroundButtonProps) {
    const content = useMemo(() => {
        if (props.background === 'none') {
            return <span className="text-[12px]">None</span>
        }
        if (props.background === 'blur') {
            return <span className="text-[12px]">Blur</span>
        } else {
            return (
                <img
                    src={props.background.thumb}
                    alt={props.background.name}
                    className="w-full h-full object-cover rounded-[14px]"
                />
            )
        }
    }, [props.active])

    return (
        <Tooltip text={props.name}>
            <Button
                className={cn(
                    'w-[44px] h-[44px] rounded-[14px] hover:ring-2 ring-white/40',
                    props.background === 'none' && 'bg-neutral-700',
                    props.background === 'blur' && 'bg-radial from-gray-800 to-gray-500',
                    props.active && 'ring-2 ring-muse-yellow focus-visible:ring-0'
                )}
                onClick={props.onClick}
            >
                {content}
            </Button>
        </Tooltip>
    )
}
